/**
 * Preset Adapter
 * Converts web-app preset data to the new PresetOption type
 */

import type { PresetCategory, PresetOption } from '../components/PresetSelector/PresetSelector.types';
import type { Preset } from '../types';

/**
 * Web-app preset structure (from appstore.js globalPresets and singlePresets)
 */
export interface WebAppPreset {
  _id?: string;
  id?: string;
  name: string;
  fileType?: string;
  fileSize?: string;
  customWidth?: number;
  customHeight?: number;
  isGlobal?: boolean;
}

/**
 * Convert a single web-app preset object to the new PresetOption interface
 * 
 * @param preset - Web-app preset object
 * @param isOriginal - Whether this represents the original file (no transformation)
 * @returns PresetOption object compatible with PresetSelector component
 * 
 * @example
 * ```typescript
 * const webAppPreset = {
 *   _id: 'preset123',
 *   name: 'Thumbnail',
 *   fileType: 'jpg',
 *   customWidth: 150,
 *   customHeight: 150
 * };
 * 
 * const preset = adaptPreset(webAppPreset);
 * // Returns: { id: 'preset123', name: 'Thumbnail', fileType: 'jpg', dimensions: { width: 150, height: 150 } }
 * ```
 */
export function adaptPreset(preset: WebAppPreset, isOriginal: boolean = false): PresetOption {
  const id = preset._id || preset.id || '';
  const dimensions = 
    preset.customWidth && preset.customHeight
      ? { width: preset.customWidth, height: preset.customHeight }
      : undefined;

  return {
    id,
    name: preset.name,
    fileType: preset.fileType,
    fileSize: preset.fileSize,
    customWidth: preset.customWidth,
    customHeight: preset.customHeight,
    dimensions,
    isOriginal,
  };
}

/**
 * Create an "Original" preset option for when no transformation is needed
 * 
 * @param name - Display name for the original option
 * @returns PresetOption representing the original file
 * 
 * @example
 * ```typescript
 * const originalPreset = createOriginalPreset();
 * // Returns: { id: 'original', name: 'Original', isOriginal: true }
 * ```
 */
export function createOriginalPreset(name: string = 'Original'): PresetOption {
  return {
    id: 'original',
    name,
    isOriginal: true,
  };
}

/**
 * Convert web-app single presets and global presets into PresetOption array
 * 
 * @param singlePresets - Asset-specific presets from the API
 * @param globalPresets - Global presets available for all assets
 * @param includeOriginal - Whether to include the "Original" option
 * @returns Array of PresetOption objects
 * 
 * @example
 * ```typescript
 * const singlePresets = resource.presets || [];
 * const globalPresets = appStore.globalPresets || [];
 * 
 * const presets = adaptPresets(singlePresets, globalPresets);
 * ```
 */
export function adaptPresets(
  singlePresets: WebAppPreset[] = [],
  globalPresets: WebAppPreset[] = [],
  includeOriginal: boolean = true
): PresetOption[] {
  const result: PresetOption[] = [];

  // Add original option first if requested
  if (includeOriginal) {
    result.push(createOriginalPreset());
  }

  // Add single presets (asset-specific)
  if (Array.isArray(singlePresets)) {
    for (const preset of singlePresets) {
      result.push(adaptPreset(preset));
    }
  }

  // Add global presets
  if (Array.isArray(globalPresets)) {
    for (const preset of globalPresets) {
      result.push(adaptPreset(preset));
    }
  }

  return result;
}

/**
 * Convert web-app presets into categorized PresetCategory array
 * 
 * @param singlePresets - Asset-specific presets
 * @param globalPresets - Global presets
 * @param includeOriginal - Whether to include the "Original" option
 * @returns Array of PresetCategory objects for categorized display
 * 
 * @example
 * ```typescript
 * const categories = adaptPresetsWithCategories(singlePresets, globalPresets);
 * // Returns: [
 * //   { label: 'File', presets: [{ name: 'Original', isOriginal: true }] },
 * //   { label: 'Asset Presets', presets: [...] },
 * //   { label: 'Global Presets', presets: [...] }
 * // ]
 * ```
 */
export function adaptPresetsWithCategories(
  singlePresets: WebAppPreset[] = [],
  globalPresets: WebAppPreset[] = [],
  includeOriginal: boolean = true
): PresetCategory[] {
  const categories: PresetCategory[] = [];

  // Original file category
  if (includeOriginal) {
    categories.push({
      label: 'File',
      presets: [createOriginalPreset()],
    });
  }

  // Asset-specific presets
  if (Array.isArray(singlePresets) && singlePresets.length > 0) {
    categories.push({
      label: 'Asset Presets',
      presets: singlePresets.map((p) => adaptPreset(p)),
    });
  }

  // Global presets
  if (Array.isArray(globalPresets) && globalPresets.length > 0) {
    categories.push({
      label: 'Global Presets',
      presets: globalPresets.map((p) => adaptPreset(p)),
    });
  }

  return categories;
}

/**
 * Find a preset by ID from the combined list of single and global presets
 * 
 * @param presetId - ID of the preset to find
 * @param singlePresets - Asset-specific presets
 * @param globalPresets - Global presets
 * @returns The found WebAppPreset or undefined
 */
export function findWebAppPreset(
  presetId: string,
  singlePresets: WebAppPreset[] = [],
  globalPresets: WebAppPreset[] = []
): WebAppPreset | undefined {
  // Check single presets first
  const singlePreset = singlePresets.find(
    (p) => p._id === presetId || p.id === presetId
  );
  if (singlePreset) return singlePreset;

  // Check global presets
  return globalPresets.find(
    (p) => p._id === presetId || p.id === presetId
  );
}

/**
 * Get the full preset information (merged from web-app format)
 * This replicates the getFullPreset logic from messages.js
 * 
 * @param presetId - ID of the preset
 * @param singlePresets - Asset-specific presets
 * @param globalPresets - Global presets
 * @returns Preset information or undefined if not found
 */
export function getFullPreset(
  presetId: string | undefined,
  singlePresets: WebAppPreset[] = [],
  globalPresets: WebAppPreset[] = []
): Preset | undefined {
  if (!presetId || presetId === 'original') {
    return undefined;
  }

  const webAppPreset = findWebAppPreset(presetId, singlePresets, globalPresets);
  if (!webAppPreset) {
    return undefined;
  }

  return {
    id: webAppPreset._id || webAppPreset.id || '',
    name: webAppPreset.name,
    fileType: webAppPreset.fileType,
    fileSize: webAppPreset.fileSize,
    customWidth: webAppPreset.customWidth,
    customHeight: webAppPreset.customHeight,
  };
}

/**
 * Type guard to check if an object is a valid WebAppPreset
 */
export function isWebAppPreset(obj: unknown): obj is WebAppPreset {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  
  const preset = obj as Record<string, unknown>;
  return typeof preset.name === 'string';
}